import { ref, computed, onMounted, watch, provide } from "vue";
import L from "leaflet";
import { useStore } from "vuex";

export function useHolesView() {
  const store = useStore();

  const courses = computed(() => store.getters["courses/getCourseNames"]);
  const holes = computed(() => store.getters["holes/getHoles"]);
  const applyBounds = ref(true);
  const boundsMapAndFlags = computed(
    () => store.getters["holes/getBoundsMapAndFlags"]
  );
  const enableSendToDevice = computed(
    () => store.getters["holes/getEnableSendToDevice"]
  );
  const loading = computed(() => store.getters["holes/getLoading"]);
  const targetCourse = ref(null);

  provide("targetCourse", targetCourse);

  const showImportModal = ref(false);
  const showClearModal = ref(false);

  const openImportHoles = () => {
    showImportModal.value = true;
  };

  const openClearHoles = () => {
    showClearModal.value = true;
  };

  const confirmClearHoles = async () => {
    if (targetCourse.value) {
      try {
        await store.dispatch("holes/clearHoles", targetCourse.value.id);
        showClearModal.value = false;
      } catch (error) {
        // Errors are already surfaced to the user via the toast notification
        // dispatched inside the clearHoles action. Avoid logging here to
        // prevent debug output from leaking to production logs.
      }
    }
  };

  const myMap = ref(null);
  const selectedCourse = computed(
    () => store.getters["holes/getSelectedCourse"]
  );
  const source = ref({ name: "Course holes" });
  const hole = ref(0);

  const showCourseMap = ref(true);

  const zoom = ref(15.5);
  const center = ref([51.505117019117044, -0.15981318574761885]);
  const url = ref(
    "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
  );
  const enableSaveHole = ref(false);
  const opacity = ref(0.6);
  const overlay = ref("");
  const mapImage = ref("");
  const bounds = ref([
    [0, 0],
    [0, 0],
  ]);
  const mapRotation = ref(0);
  const sameCenter = ref(true);
  const icon = ref(
    L.icon({
      iconUrl: "/images/dot-circle.svg",
      iconSize: [12, 12],
    })
  );

  const selectedHole = computed(() => store.getters["holes/getSelectedHole"]);

  const centerUpdated = (newCenter) => {
    center.value = newCenter;
    sameCenter.value = false;
  };

  const reCenter = () => {
    if (myMap.value) {
      myMap.value.leafletObject.setView(center.value, zoom.value);
      sameCenter.value = true;
    }
  };

  const changeView = (satellite) => {
    showCourseMap.value = !satellite;
  };

  const setCourse = async (course) => {
    store.commit("holes/SET_SELECTED_COURSE", course);
  };

  const setCenterFromHole = (holeData) => {
    if (
      holeData &&
      holeData.centerPoint &&
      Array.isArray(holeData.centerPoint)
    ) {
      store.commit("holes/SET_SELECTED_HOLE", holeData);
      center.value = holeData.centerPoint;
      if (myMap.value) {
        myMap.value.leafletObject.setView(center.value, zoom.value);
      }
    }
  };

  const updateTee = (latlng) => {
    if (selectedHole.value) {
      store.commit("holes/SET_SELECTED_HOLE", {
        ...selectedHole.value,
        startPoint: [latlng.lat, latlng.lng],
      });
    }
  };

  const updateFront = (latlng) => {
    if (selectedHole.value) {
      store.commit("holes/SET_SELECTED_HOLE", {
        ...selectedHole.value,
        frontPoint: [latlng.lat, latlng.lng],
      });
    }
  };

  const updateCenter = (latlng) => {
    if (selectedHole.value) {
      store.commit("holes/SET_SELECTED_HOLE", {
        ...selectedHole.value,
        centerPoint: [latlng.lat, latlng.lng],
      });
    }
  };

  const updateBack = (latlng) => {
    if (selectedHole.value) {
      store.commit("holes/SET_SELECTED_HOLE", {
        ...selectedHole.value,
        endPoint: [latlng.lat, latlng.lng],
      });
    }
  };

  const saveHoles = async () => {
    await store.dispatch("holes/updateNodeCourseHoles", {
      courseId: targetCourse.value.id,
      holes: holes.value,
    });
  };

  const sendConfig = async () => {
    await store.dispatch("holes/sendConfigToDevices", targetCourse.value.id);
  };

  const applyBoundsAndOverlays = () => {
    if (!boundsMapAndFlags.value || !targetCourse.value) return;

    const {
      northEastLatitude,
      northEastLongitude,
      southWestLatitude,
      southWestLongitude,
      mapRotation: rotation,
    } = boundsMapAndFlags.value;

    bounds.value = [
      [southWestLatitude, southWestLongitude],
      [northEastLatitude, northEastLongitude],
    ];

    mapImage.value = boundsMapAndFlags.value.map;
    overlay.value = boundsMapAndFlags.value.flags;

    zoom.value = parseFloat(boundsMapAndFlags.value.zoom);

    mapRotation.value = rotation || 0;

    const centerLat = (northEastLatitude + southWestLatitude) / 2;
    const centerLng = (northEastLongitude + southWestLongitude) / 2;
    center.value = [centerLat, centerLng];

    if (myMap.value && myMap.value.leafletObject) {
      myMap.value.leafletObject.fitBounds(bounds.value);
    }
  };

  const resetToDefualt = () => {
    zoom.value = 15.5;
    center.value = [51.505117019117044, -0.15981318574761885];
  };

  watch(
    () => hole.value,
    (newVal) => {
      if (holes.value.length > 0 && holes.value[newVal]) {
        setCenterFromHole(holes.value[newVal]);
      }
    }
  );

  watch(
    () => targetCourse.value,
    async (newVal) => {
      if (newVal) {
        setCourse(newVal.id);
        try {
          store.commit("holes/SET_LOADING", true);
          await store.dispatch("holes/fetchBoundsMapAndFlags", newVal.id);
          applyBoundsAndOverlays();
          await store.dispatch("holes/fetchHoles", newVal.id);
          if (holes.value.length > 0) {
            applyBoundsAndOverlays();
          }
        } catch (error) {
          resetToDefualt();
        } finally {
          store.commit("holes/SET_LOADING", false);
        }
      } else {
        setCourse(null);
        resetToDefualt();
        store.commit("holes/SET_HOLES", []);
      }
    }
  );

  watch(
    () => boundsMapAndFlags.value,
    (newVal) => {
      if (newVal) {
        applyBoundsAndOverlays();
      }
    }
  );

  watch(
    () => holes.value,
    (newVal) => {
      if (newVal) {
        if (applyBounds.value) {
          applyBoundsAndOverlays();
          store.commit("holes/SET_SELECTED_HOLE", holes.value[hole.value]);
        }
      }
    }
  );

  watch(
    () => selectedHole.value,
    (newVal, oldValue) => {
      if (newVal && oldValue !== null) {
        enableSaveHole.value = true;

        const updatedHoles = [...holes.value];
        const holeIndex = updatedHoles.findIndex((h) => h._id === newVal._id);

        if (holeIndex !== -1) {
          updatedHoles[holeIndex] = newVal;
          applyBounds.value = false;
          store.commit("holes/SET_HOLES", updatedHoles);
          setTimeout(() => {
            applyBounds.value = true;
          }, 200);
        }
      }
    },
    { deep: true }
  );

  onMounted(() => {
    center.value = [51.505117019117044, -0.15981318574761885];
    store.dispatch("courses/fetchCourseNames");
  });

  return {
    myMap,
    selectedCourse,
    source,
    holes,
    hole,
    loading,
    showCourseMap,
    zoom,
    center,
    url,
    opacity,
    overlay,
    mapImage,
    bounds,
    sameCenter,
    icon,
    selectedHole,
    targetCourse,
    boundsMapAndFlags,
    courses,
    mapRotation,
    showImportModal,
    showClearModal,
    enableSaveHole,
    enableSendToDevice,
    openImportHoles,
    openClearHoles,
    confirmClearHoles,
    centerUpdated,
    reCenter,
    changeView,
    updateTee,
    updateFront,
    updateCenter,
    updateBack,
    saveHoles,
    sendConfig,
    applyBoundsAndOverlays,
  };
}
